import React from 'react';

/*
  Airshow background: a few small planes cross the screen, occasionally
  pulling up into a full loop, trailing airshow smoke. Drawn on a fixed,
  full-screen canvas behind the page content. Clicks pass straight through.

  Props:
    color – plane + smoke color (defaults to white)
*/

const MAX_PLANES = 3;          // planes on screen at once
const SPAWN_GAP = [1.5, 4];    // seconds between new planes (min, max)
const TRAIL_SECONDS = 2.2;     // how long smoke lingers
const LOOP_CHANCE = 0.35;      // per second while cruising, once on screen

function rand(min, max) {
  return min + Math.random() * (max - min);
}

function makePlane(w, h) {
  const goingRight = Math.random() < 0.5;
  const scale = rand(0.7, 1.15) * (w < 600 ? 0.75 : 1);
  return {
    dir: goingRight ? 1 : -1,
    x: goingRight ? -60 : w + 60,
    baseY: rand(h * 0.12, h * 0.8),
    y: 0,
    heading: goingRight ? 0 : Math.PI,
    speed: rand(140, 230) * (w < 600 ? 0.7 : 1),   // px per second
    scale,
    t: rand(0, 10),                // phase for the gentle cruise wobble
    wobble: rand(8, 22),
    state: 'cruise',
    loopTurned: 0,
    loopRadius: rand(55, 95) * scale,
    loopsLeft: Math.random() < 0.25 ? 2 : 1,
    hasLooped: false,
    trail: [],
  };
}

// Side-profile high-wing plane, nose pointing +x, centered at origin.
function drawPlane(ctx) {
  ctx.beginPath();
  // fuselage
  ctx.moveTo(22, 0);
  ctx.quadraticCurveTo(20, -6, 8, -7);
  ctx.lineTo(-14, -4);
  ctx.lineTo(-24, -3);
  ctx.lineTo(-24, 1);
  ctx.lineTo(-14, 2);
  ctx.lineTo(8, 5);
  ctx.quadraticCurveTo(20, 5, 22, 0);
  ctx.closePath();
  ctx.fill();

  // high wing
  ctx.fillRect(-4, -10, 18, 3);
  // tail fin
  ctx.beginPath();
  ctx.moveTo(-15, -4);
  ctx.lineTo(-24, -15);
  ctx.lineTo(-27, -15);
  ctx.lineTo(-24, -3);
  ctx.closePath();
  ctx.fill();
  // horizontal stabilizer
  ctx.fillRect(-28, -2, 9, 2);
  // propeller blur
  ctx.fillRect(23, -7, 1.5, 14);
  // wheels
  ctx.beginPath();
  ctx.arc(10, 8, 2, 0, Math.PI * 2);
  ctx.arc(-4, 8, 2, 0, Math.PI * 2);
  ctx.fill();
}

class Airplanes extends React.Component {
  constructor(props) {
    super(props);
    this.canvasRef = React.createRef();
    this.planes = [];
    this.nextSpawn = 0.3;
    this.last = null;
    this.frame = null;
    this.tick = this.tick.bind(this);
    this.resize = this.resize.bind(this);
  }

  componentDidMount() {
    const reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    this.resize();
    window.addEventListener('resize', this.resize);
    this.frame = requestAnimationFrame(this.tick);
  }

  componentWillUnmount() {
    cancelAnimationFrame(this.frame);
    window.removeEventListener('resize', this.resize);
  }

  resize() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    this.w = window.innerWidth;
    this.h = window.innerHeight;
    canvas.width = this.w * dpr;
    canvas.height = this.h * dpr;
    canvas.style.width = this.w + 'px';
    canvas.style.height = this.h + 'px';
    this.ctx = canvas.getContext('2d');
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  update(p, dt, now) {
    // once a plane has left the screen it stops; its smoke just fades out
    const drop = () => {
      while (p.trail.length && now - p.trail[0].time > TRAIL_SECONDS) p.trail.shift();
    };
    if (p.gone) return drop();

    p.t += dt;

    if (p.state === 'cruise') {
      // gentle climb/descend wobble around the base altitude
      const targetY = p.baseY + Math.sin(p.t * 0.9) * p.wobble;
      const dy = targetY - p.y;
      p.heading = p.dir === 1 ? Math.atan2(dy, 40) : Math.PI - Math.atan2(dy, 40);

      const onScreen = p.x > this.w * 0.15 && p.x < this.w * 0.85;
      if (onScreen && !p.hasLooped && Math.random() < LOOP_CHANCE * dt) {
        p.state = 'loop';
        p.loopTurned = 0;
        p.heading = p.dir === 1 ? 0 : Math.PI;
      }
    } else {
      // pull up: turn at a constant rate until a full 360° is done
      const omega = p.speed / p.loopRadius;
      const turn = omega * dt;
      p.heading += p.dir === 1 ? -turn : turn;
      p.loopTurned += turn;
      if (p.loopTurned >= Math.PI * 2) {
        p.loopsLeft -= 1;
        p.loopTurned = 0;
        p.heading = p.dir === 1 ? 0 : Math.PI;
        if (p.loopsLeft <= 0) {
          p.state = 'cruise';
          p.hasLooped = true;
          p.baseY = p.y;
          p.t = 0; // restart the wobble level with the new altitude
        }
      }
    }

    p.x += Math.cos(p.heading) * p.speed * dt;
    p.y += Math.sin(p.heading) * p.speed * dt;

    p.trail.push({
      x: p.x - Math.cos(p.heading) * 24 * p.scale,
      y: p.y - Math.sin(p.heading) * 24 * p.scale,
      time: now,
    });
    drop();

    const off = p.x < -80 || p.x > this.w + 80 || p.y < -150 || p.y > this.h + 150;
    if (off) p.gone = true;
  }

  draw(p, now) {
    const ctx = this.ctx;

    // smoke trail, fading and widening with age
    for (let i = 1; i < p.trail.length; i++) {
      const a = p.trail[i - 1];
      const b = p.trail[i];
      const age = (now - b.time) / TRAIL_SECONDS;
      ctx.globalAlpha = Math.max(0, 0.35 * (1 - age));
      ctx.lineWidth = (2 + age * 7) * p.scale;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }

    if (p.gone) return;

    // the plane itself; mirror left-flying planes so they fly upright
    ctx.globalAlpha = 0.9;
    ctx.save();
    ctx.translate(p.x, p.y);
    if (p.dir === 1) {
      ctx.rotate(p.heading);
    } else {
      ctx.rotate(p.heading - Math.PI);
      ctx.scale(-1, 1);
    }
    ctx.scale(p.scale, p.scale);
    drawPlane(ctx);
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  tick(ms) {
    const now = ms / 1000;
    // clamp dt so returning to a background tab doesn't teleport planes
    const dt = this.last === null ? 0 : Math.min(now - this.last, 0.05);
    this.last = now;

    this.nextSpawn -= dt;
    if (this.nextSpawn <= 0 && this.planes.length < MAX_PLANES) {
      const p = makePlane(this.w, this.h);
      p.y = p.baseY;
      this.planes.push(p);
      this.nextSpawn = rand(SPAWN_GAP[0], SPAWN_GAP[1]);
    }

    const ctx = this.ctx;
    const color = this.props.color || '#ffffff';
    ctx.clearRect(0, 0, this.w, this.h);
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.lineCap = 'round';

    this.planes.forEach(p => {
      this.update(p, dt, now);
      this.draw(p, now);
    });

    // remove planes that have left the screen once their smoke has cleared
    this.planes = this.planes.filter(p => !(p.gone && p.trail.length === 0));

    this.frame = requestAnimationFrame(this.tick);
  }

  render() {
    return (
      <canvas
        ref={this.canvasRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: -1,
          pointerEvents: 'none',
        }}
      />
    );
  }
}

export default Airplanes;
